import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { toast } from 'sonner'
import type { Guide } from '@/lib/guides/types/guide'
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
} from '@/components/app/panels/left-panel/type-panel/type-panel-presets'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { NUDGE_MAX_PT } from '@/api/services/restorable-settings.service'
import { tryUngroupSelection } from '@/lib/groups/ungroup-selection'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import type { CanvasArrangeCommand, CanvasKeyAction } from '../helpers/canvas-key-action'
import { duplicateSelection } from './duplicate-selection'
import { resolveFirstRunPointSize } from '../helpers/first-run-point-size'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { MarqueeZoomDragState } from '../types/canvas-drag-state'

/** Everything the canvas keyboard shortcuts read and drive, as captured by their keydown effect. */
export type CanvasKeyActionContext = {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  scrollRef: MutableRefObject<HTMLDivElement | null>
  applyPanScroll: (left: number, top: number) => unknown
  bleedWidth: number
  bleedHeight: number
  activeItem: string
  isInShapeCreationMode: boolean
  isNodeEditMode: boolean
  nodeEditShapeId: string | null
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  isTextToolActive: boolean
  isTextLinkingActive: boolean
  cancelTextLinking: () => void
  activeIsolationGroupId: string | null
  exitGroupIsolationMode: () => void
  setGroupIsolationPath: Dispatch<SetStateAction<string[]>>
  zMarqueeKeyHeldRef: MutableRefObject<boolean>
  setZMarqueeKeyHeld: Dispatch<SetStateAction<boolean>>
  setIsMarqueeZoomMode: Dispatch<SetStateAction<boolean>>
  setMarqueeZoom: Dispatch<SetStateAction<MarqueeZoomDragState | null>>
  selectedGuideIdsRef: MutableRefObject<Set<string>>
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
  updateGuides: (next: Guide[] | ((prev: Guide[]) => Guide[])) => void
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
  clearUnifiedSelectionWithHistory: () => void
  copySelectionToClipboard: () => boolean
  cutSelectionToClipboard: () => boolean
  cutSelectionForActiveMode: () => boolean
  pasteFromClipboard: (
    inPlace: boolean,
    canvasPoint?: { canvasX: number; canvasY: number } | null,
    offsetPt?: number
  ) => boolean
  isTextEditSessionActive: () => boolean
  selectAllForActiveTextEdit: () => boolean
  handleDeleteKeyWhileTextEditing: (
    e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>
  ) => void
  refocusActiveTextEditor: () => void
  deleteSelectedObjects: () => 'none' | 'blocked' | 'deleted'
  lockSelectedObjects: () => void
  unlockSelectedObjects: () => void
  toggleFreezeSelectedObjects: () => void
  canUnifiedBringForward: (selectedIds: string[]) => boolean
  canUnifiedSendBackward: (selectedIds: string[]) => boolean
  nudgeDistancePt: number
  onShapesNudge: CenterCanvasConfig['onShapesNudge']
  onLayerReorder: CenterCanvasConfig['onLayerReorder']
  onUnifiedLayerArrange: CenterCanvasConfig['onUnifiedLayerArrange']
  onDeactivatePanMode: CenterCanvasConfig['onDeactivatePanMode']
  onSelectAllApplied: CenterCanvasConfig['onSelectAllApplied']
  onSelectNoneApplied: CenterCanvasConfig['onSelectNoneApplied']
  onBackgroundClick: CenterCanvasConfig['onBackgroundClick']
  onDeleteShapesHandled: CenterCanvasConfig['onDeleteShapesHandled']
  onActivateTextTool: CenterCanvasConfig['onActivateTextTool']
  onActivateSelectTool: CenterCanvasConfig['onActivateSelectTool']
}

type C = CanvasKeyActionContext

function deactivatePanMode(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  c.onDeactivatePanMode?.()
}

function holdMarqueeZoom(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  c.zMarqueeKeyHeldRef.current = true
  c.setZMarqueeKeyHeld(true)
  if (!e.repeat) {
    c.setIsMarqueeZoomMode((v) => !v)
    c.setMarqueeZoom(null)
  }
}

function selectNone(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  c.clearUnifiedSelectionWithHistory()
  c.onSelectNoneApplied?.()
}

function selectAll(e: KeyboardEvent, isEditable: boolean, c: C) {
  // Text-editing mode is live manager state, not a property of the event target — the same
  // reason the Ctrl+X branch below checks it. A `data-text-edit-keepalive` control (the
  // whole Type panel root) keeps the session open while focus sits on a plain <button> that
  // `isEditableTarget` does not match, so clicking Bold mid-edit and pressing Ctrl+A landed
  // here and select-alled every OBJECT on the spread; the Ctrl+X that usually follows then
  // cut the entire spread (WMPCM-9396).
  if (c.isTextEditSessionActive()) {
    e.preventDefault()
    c.selectAllForActiveTextEdit()
    return
  }

  if (isEditable) return
  e.preventDefault()
  c.onActivateSelectTool?.()
  c.internalShapeManagerRef.current?.selectAll()
  c.onSelectAllApplied?.()
}

function lockSelection(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  if (c.activeItem !== 'select') return
  e.preventDefault()
  if (e.shiftKey) {
    c.unlockSelectedObjects()
  } else {
    c.lockSelectedObjects()
  }
}

function toggleFreeze(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  c.toggleFreezeSelectedObjects()
}

function copy(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  if (c.copySelectionToClipboard()) {
    e.preventDefault()
  }
}

function cut(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return

  // Text-editing mode is live manager state, not a property of the event target. A
  // `data-text-edit-keepalive` control keeps the edit session alive while focus sits on a
  // plain <button> that `isEditableTarget` doesn't match — the whole Type panel root carries
  // that attribute, so clicking Bold mid-edit lands here. Falling through would cut the very
  // frame the user is editing (WMPCM-7004); route to the text cut instead.
  if (c.isTextEditSessionActive()) {
    e.preventDefault()
    c.cutSelectionForActiveMode()
    return
  }

  if (c.cutSelectionToClipboard()) {
    e.preventDefault()
  }
}

function paste(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  const pasted = c.pasteFromClipboard(e.shiftKey)

  if (pasted) {
    e.preventDefault()
  }
}

function duplicate(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return

  // Mid text edit (focus on a `data-text-edit-keepalive` control, so `isEditable` is false)
  // Duplicate does nothing — but still swallow the browser's own Ctrl/Cmd+D (bookmark), the way
  // the Ctrl+X branch claims the key while text editing.
  if (c.isTextEditSessionActive()) {
    e.preventDefault()
    return
  }

  if (duplicateSelection(c)) {
    e.preventDefault()
  }
}

function suppressFind(e: KeyboardEvent, isEditable: boolean) {
  if (isEditable) return
  e.preventDefault()
}

function group(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  const mgr = c.internalShapeManagerRef.current

  if (!mgr) return
  e.preventDefault()
  if (e.shiftKey) {
    tryUngroupSelection(mgr, (message) => toast.error(message))
  } else {
    mgr.groupSelectedObjects()
  }
}

function exitAllIsolation(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  // Runs before the Delete/Backspace branch below, so it needs the same live-session guard —
  // otherwise Shift+Backspace from a keepalive control still yanks the user out of group
  // isolation mid-edit (WMPCM-7044). Hand focus back rather than just swallowing the press:
  // returning bare left the keystroke dead — no isolation exit, no text edit, and focus
  // still parked on the panel control, so the user's NEXT press went nowhere either.
  if (c.isTextEditSessionActive()) {
    e.preventDefault()
    c.refocusActiveTextEditor()
    return
  }

  e.preventDefault()
  c.exitGroupIsolationMode()
}

function activateTextTool(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  if (e.ctrlKey || e.metaKey) return
  e.preventDefault()
  c.onActivateTextTool?.()
}

function deleteSelection(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return

  // Text-editing mode is live manager state, not a property of the event target — the same
  // gap the Ctrl+X branch above documents. A `data-text-edit-keepalive` control (the whole
  // Type panel root, the whole menubar dropdown) keeps the edit session alive while focus
  // sits on a plain <button> that `isEditableTarget` doesn't match, so clicking Bold mid-edit
  // and pressing Delete landed here and destroyed the frame being edited (WMPCM-7044).
  //
  // Placed ahead of the guides branch on purpose: a keystroke the text editor owns must not
  // delete ruler guides either. The guard itself is deliberately modifier-agnostic, matching
  // the enclosing `if`, so Ctrl+Backspace (delete-word) and Alt+Backspace can't fall through
  // either — see `handleDeleteKeyWhileTextEditing` for how those are handled.
  if (c.isTextEditSessionActive()) {
    e.preventDefault()
    c.handleDeleteKeyWhileTextEditing(e)
    return
  }

  if (c.selectedGuideIdsRef.current.size > 0) {
    e.preventDefault()
    const ids = c.selectedGuideIdsRef.current

    c.updateGuides((prev) => prev.filter((g) => !ids.has(g.id)))
    c.setSelectedGuideIds(new Set())
    return
  }

  const deleteResult = c.deleteSelectedObjects()

  if (deleteResult === 'deleted') {
    e.preventDefault()
    c.onDeleteShapesHandled?.()
  } else if (deleteResult === 'blocked') {
    e.preventDefault()
  }
}

function panByArrow(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  const step = e.shiftKey ? 100 : 10
  const scroller = c.scrollRef.current

  if (!scroller) return

  let nextLeft = scroller.scrollLeft
  let nextTop = scroller.scrollTop

  if (e.key === 'ArrowUp') nextTop -= step
  if (e.key === 'ArrowDown') nextTop += step
  if (e.key === 'ArrowLeft') nextLeft -= step
  if (e.key === 'ArrowRight') nextLeft += step

  c.applyPanScroll(nextLeft, nextTop)
}

function nudgeGuides(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  e.preventDefault()
  e.stopPropagation()
  const focused = document.activeElement as HTMLElement | null

  if (focused && focused !== document.body) {
    focused.blur()
  }

  const step = e.shiftKey ? 10 : 1
  const ids = c.selectedGuideIdsRef.current

  c.updateGuides((prev) =>
    prev.map((g) => {
      if (!ids.has(g.id)) return g
      let d = 0

      if (g.type === 'horizontal') {
        if (e.key === 'ArrowUp') d = -step
        if (e.key === 'ArrowDown') d = step
      } else {
        if (e.key === 'ArrowLeft') d = -step
        if (e.key === 'ArrowRight') d = step
      }

      if (d) {
        const maxPos = g.type === 'horizontal' ? c.bleedHeight : c.bleedWidth

        return { ...g, position: Math.max(0, Math.min(maxPos, g.position + d)) }
      }

      return g
    })
  )
}

/** Returns false when no single text frame is selected — the keystroke is not this action's. */
function fontSizeStep(e: KeyboardEvent, c: C): boolean {
  const mgr = c.internalShapeManagerRef.current
  const shapeIds = mgr?.getSelectedShapeIds() ?? []

  if (shapeIds.length === 1) {
    const shape = mgr?.getShapes().find((s) => s.id === shapeIds[0])

    if (shape && isShapeTextFrame(shape)) {
      const current = resolveFirstRunPointSize(shape.paragraphs)

      e.preventDefault()
      mgr?.applyShapeTextRunStyle({
        pointSize: Math.max(
          FONT_SIZE_MIN,
          Math.min(FONT_SIZE_MAX, Math.round(current) + (e.key === 'ArrowUp' ? 1 : -1))
        ),
      })
      return true
    }
  }

  return false
}

function nudgeShapes(e: KeyboardEvent, isEditable: boolean, c: C) {
  if (isEditable) return
  if (e.ctrlKey || e.metaKey || e.altKey) return
  const mgr = c.internalShapeManagerRef.current
  const shapeIds = mgr?.getSelectedShapeIds() ?? []

  if (shapeIds.length === 0) return

  const baseNudge = Math.max(1, Math.round(Number(c.nudgeDistancePt))) || 1
  const step = e.shiftKey ? Math.min(NUDGE_MAX_PT, baseNudge * 10) : baseNudge
  let dx = 0
  let dy = 0

  if (e.key === 'ArrowUp') dy = -step
  if (e.key === 'ArrowDown') dy = step
  if (e.key === 'ArrowLeft') dx = -step
  if (e.key === 'ArrowRight') dx = step
  if (dx === 0 && dy === 0) return

  let handled = false

  if (c.onShapesNudge && mgr) {
    const items = mgr.getSnappedNudgeItems(dx, dy)

    if (items.length > 0) {
      c.onShapesNudge(items)
      handled = true
    }
  }

  if (handled) {
    e.preventDefault()
  }
}

/** `]` / `[` (to front / back) and Ctrl/Cmd+`]` / `[` (one step), gated on the unified order. */
function arrange(e: KeyboardEvent, isEditable: boolean, c: C, command: CanvasArrangeCommand) {
  if (isEditable) return
  const mgr = c.internalShapeManagerRef.current
  const shapeIds = mgr?.getSelectedShapeIds() ?? []

  if (shapeIds.length === 0) return
  const canArrange =
    command === 'bringToFront' || command === 'bringForward'
      ? c.canUnifiedBringForward
      : c.canUnifiedSendBackward

  if (!canArrange(shapeIds)) return
  e.preventDefault()

  const result = mgr?.[command]()

  if (result && c.onLayerReorder) {
    c.onLayerReorder(result.beforeOrder, result.afterOrder)
  }

  c.onUnifiedLayerArrange?.(shapeIds, command)
}

/** Leaves node edit mode with the edited shape selected (Enter, and Escape's node-edit step). */
function exitNodeEditKeepingSelection(e: KeyboardEvent, c: C) {
  e.preventDefault()
  const editId = c.nodeEditShapeId

  c.exitNodeEditMode(editId ?? undefined)
  if (editId) {
    c.internalShapeManagerRef.current?.selectShapes([editId])
  }
}

function escape(e: KeyboardEvent, isEditable: boolean, c: C) {
  // Read before any branch below calls `preventDefault` itself. A right-panel Escape chain or a
  // Radix popover that already consumed this press (closed a dropdown, preview or panel) leaves
  // it set, and the final deselect must not also fire on that same press.
  const handledElsewhere = e.defaultPrevented

  // An in-progress single-object resize or rotate drag takes priority over every other
  // Escape behavior below (deselect, mode exit, etc.) — cancel and revert it instead of
  // falling through, since the window pointermove/pointerup listeners are still live and
  // would otherwise commit the gesture on the next pointerup regardless of what Escape did.
  if (c.internalShapeManagerRef.current?.cancelActiveResize()) {
    e.preventDefault()
    return
  }

  if (c.internalShapeManagerRef.current?.cancelActiveRotate()) {
    e.preventDefault()
    return
  }

  if (c.isTextLinkingActive) {
    e.preventDefault()
    c.cancelTextLinking()
    return
  }

  if (isEditable) return
  if (c.isInShapeCreationMode) return

  // Node edit mode: first Escape exits node edit; shape stays selected → bounding box shows
  if (c.isNodeEditMode) {
    exitNodeEditKeepingSelection(e, c)
    return
  }

  c.setIsMarqueeZoomMode(false)
  c.zMarqueeKeyHeldRef.current = false
  c.setZMarqueeKeyHeld(false)
  c.setMarqueeZoom(null)

  const shapeMgr = c.internalShapeManagerRef.current

  if (shapeMgr?.getEditingShapeTextId()) {
    e.preventDefault()
    shapeMgr.exitShapeTextEdit({ activateSelectTool: true })
    return
  }

  if (c.isTextToolActive) {
    e.preventDefault()
    c.onActivateSelectTool?.()
  }

  if (handledElsewhere) return

  if (c.activeIsolationGroupId) {
    c.setGroupIsolationPath((prev) => {
      const rootId = prev[0]

      if (rootId) {
        c.internalShapeManagerRef.current?.recomputePassiveGroupBounds(rootId, {
          includeNested: true,
        })
      }

      return prev.slice(0, Math.max(0, prev.length - 1))
    })
    c.clearShapeSelectionWithHistory([])
    c.setSelectedGuideIds(new Set())
    return
  }

  c.clearShapeSelectionWithHistory([])
  c.setSelectedGuideIds(new Set())
  c.onBackgroundClick?.()
}

/** Performs a resolved canvas shortcut. */
export function runCanvasKeyAction(
  action: CanvasKeyAction,
  e: KeyboardEvent,
  isEditable: boolean,
  c: CanvasKeyActionContext
): void {
  switch (action.kind) {
    case 'deactivatePanMode':
      return deactivatePanMode(e, isEditable, c)
    case 'holdMarqueeZoom':
      return holdMarqueeZoom(e, isEditable, c)
    case 'selectNone':
      return selectNone(e, isEditable, c)
    case 'selectAll':
      return selectAll(e, isEditable, c)
    case 'lockSelection':
      return lockSelection(e, isEditable, c)
    case 'toggleFreeze':
      return toggleFreeze(e, isEditable, c)
    case 'copy':
      return copy(e, isEditable, c)
    case 'cut':
      return cut(e, isEditable, c)
    case 'paste':
      return paste(e, isEditable, c)
    case 'duplicate':
      return duplicate(e, isEditable, c)
    case 'suppressFind':
      return suppressFind(e, isEditable)
    case 'group':
      return group(e, isEditable, c)
    case 'exitAllIsolation':
      return exitAllIsolation(e, isEditable, c)
    case 'activateTextTool':
      return activateTextTool(e, isEditable, c)
    case 'delete':
      return deleteSelection(e, isEditable, c)
    case 'panByArrow':
      return panByArrow(e, isEditable, c)
    case 'nudgeGuides':
      return nudgeGuides(e, isEditable, c)
    case 'fontSizeStep':
      if (fontSizeStep(e, c)) return
      if (action.fallback === 'nudgeShapes') nudgeShapes(e, isEditable, c)
      return
    case 'nudgeShapes':
      return nudgeShapes(e, isEditable, c)
    case 'arrange':
      return arrange(e, isEditable, c, action.command)
    case 'exitNodeEdit':
      return exitNodeEditKeepingSelection(e, c)
    case 'escape':
      return escape(e, isEditable, c)
  }
}
